import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import Autocomplete from "react-google-autocomplete";
import { PriceDelivery, allPolygon } from "../../server/Polygon";
import { setPreload } from "../../server/CartsOpen";
import Input from "../containers/InputForm";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import showAlert from "../../server/ShowAlert";
import {
  getExistingStreetCity,
  parseAddressFromPlace,
} from "../../utils/googleAddress";

const schema = yup
  .object({
    Name: yup
      .string()
      .required("חובה להכניס שם")
      .matches(/^[\u0590-\u05FFa-zA-Z\s]+$/, "שם חייב להכיל אותיות בלבד"),
    StreetNumber: yup
      .string()
      .required("חובה להכניס מס' בניין")
      .matches(/^\d+$/, "מספר בניין חייב להכיל ספרות בלבד"),
    HouseNumber: yup
      .string()
      .required("חובה להכניס מס' דירה")
      .matches(/^\d+$/, "מספר דירה חייב להכיל ספרות בלבד"),
    Floor: yup
      .string()
      .required("חובה להכניס קומה")
      .matches(/^-?\d+$/, "קומה חייבת להכיל ספרות בלבד"),
    ZipCode: yup.string().matches(/^\d*$/, "מיקוד חייב להכיל ספרות בלבד"),
  })
  .required();

const NewAddress = ({
  pushAddress,
  setNewAddress,
  closeSignUpPopup,
  user,
  polygonState: polygonStateProp,
}) => {
  const dispatch = useDispatch();
  const existingStreetCity = getExistingStreetCity(user);
  const [polygons, setPolygons] = useState(polygonStateProp || []);
  const [streetCityInput, setStreetCityInput] = useState(
    existingStreetCity?.query ?? "",
  );
  const [locationValidated, setLocationValidated] = useState(
    Boolean(existingStreetCity),
  );
  const [selectedAddress, setSelectedAddress] = useState(
    existingStreetCity
      ? {
          streetName: existingStreetCity.streetName,
          city: existingStreetCity.city,
        }
      : { streetName: "", city: "" },
  );
  const [deliveryMeta, setDeliveryMeta] = useState({
    lat: null,
    lng: null,
    polygonId: null,
    stationId: null,
  });
  const [addressError, setAddressError] = useState("");

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      Name: user?.name || "",
      StreetNumber: user?.houseNumber || "",
      HouseNumber: user?.apartment || "",
      Floor: user?.addrFloor || "",
      ZipCode: user?.zipCode || "",
    },
  });

  useEffect(() => {
    if (polygonStateProp?.length) {
      setPolygons(polygonStateProp);
      return;
    }

    let cancelled = false;
    allPolygon()
      .then((response) => {
        if (cancelled) return;
        const flat = response.data.map((item) => item.polygon).flat();
        setPolygons(flat);
      })
      .catch((err) => {
        console.error("Error loading delivery polygons:", err);
      });

    return () => {
      cancelled = true;
    };
  }, [polygonStateProp]);

  const resetLocationSelection = (clearInput = false) => {
    setLocationValidated(false);
    setAddressError("");
    setSelectedAddress({ streetName: "", city: "" });
    setDeliveryMeta({
      lat: null,
      lng: null,
      polygonId: null,
      stationId: null,
    });
    if (clearInput) {
      setStreetCityInput("");
    }
  };

  const setLocation = async (place) => {
    if (!place?.geometry) {
      showAlert({
        msgTitle: "אנא הזינו את שם הרחוב והעיר ובחרו מתוך האפשרויות המוצעות",
        msgTimer: "3000",
        msgType: "info",
      });
      return;
    }

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    let activePolygons = polygons;

    if (!activePolygons.length) {
      try {
        const response = await allPolygon();
        activePolygons = response.data.map((item) => item.polygon).flat();
        setPolygons(activePolygons);
      } catch {
        showAlert({
          msgTitle: "שגיאה בטעינת אזורי משלוח, נסו שוב מאוחר יותר",
          msgType: "error",
        });
        return;
      }
    }

    const deliveryMatch = PriceDelivery(lat, lng, activePolygons);
    if (!deliveryMatch) {
      resetLocationSelection(true);
      showAlert({
        msgTitle: "אופס, איננו מבצעים משלוחים לאיזור זה. עמכם הסליחה.",
        msgType: "error",
      });
      return;
    }

    const parsed = parseAddressFromPlace(place);
    if (!parsed.streetName || !parsed.city) {
      showAlert({
        msgTitle: "אנא הזינו את שם הרחוב ועיר המגורים",
        msgType: "error",
      });
      return;
    }

    const streetCityQuery = `${parsed.streetName}, ${parsed.city}`;
    setStreetCityInput(streetCityQuery);
    setSelectedAddress({
      streetName: parsed.streetName,
      city: parsed.city,
    });
    if (parsed.streetNumber) {
      setValue("StreetNumber", parsed.streetNumber, { shouldValidate: true });
    }
    if (parsed.zipCode) {
      setValue("ZipCode", parsed.zipCode);
    }

    const stationId = deliveryMatch.PolygonStationId;
    setDeliveryMeta({
      lat,
      lng,
      polygonId: stationId,
      stationId,
    });
    setAddressError("");
    setLocationValidated(true);
    showAlert({
      msgTitle: "מצויין, אנו מגיעים אליכם.",
      msgType: "success",
    });
  };

  const validateAddressSelection = () => {
    if (!locationValidated) {
      setAddressError("חובה להכניס רחוב ועיר");
      return false;
    }
    return true;
  };

  const saveAddress = async (data) => {
    if (!validateAddressSelection()) {
      return;
    }

    dispatch(setPreload(true));

    const addressToUpdate = {
      city: selectedAddress.city,
      streetName: selectedAddress.streetName,
      houseNumber: data.StreetNumber,
      apartment: data.HouseNumber,
      zipCode: data.ZipCode,
      name: data.Name,
      floor: data.Floor,
      lat: deliveryMeta.lat,
      lng: deliveryMeta.lng,
      polygonId: deliveryMeta.polygonId,
      stationId: deliveryMeta.stationId,
    };

    try {
      await pushAddress(addressToUpdate);
      if (typeof closeSignUpPopup === "function") {
        closeSignUpPopup();
      } else if (typeof setNewAddress === "function") {
        setNewAddress(false);
      }
    } catch (e) {
      console.error("Error saving address:", e);
    } finally {
      dispatch(setPreload(false));
    }
  };

  return (
    <div className="add-address">
      <form
        className="register add-address__form"
        onSubmit={handleSubmit(saveAddress, validateAddressSelection)}
      >
        <span className="note">שדות חובה מסומנים ב*</span>

        <div className="add-address__row">
          <div className="add-address__cell add-address__cell--autocomplete">
            <span>
              <label className="p-title" htmlFor="google-address-search">
                רחוב ועיר*
              </label>
              <Autocomplete
                id="google-address-search"
                className="add-address__autocomplete-input hz-field--borderless"
                value={streetCityInput}
                options={{
                  componentRestrictions: { country: "ISR" },
                  types: ["geocode"],
                }}
                onPlaceSelected={setLocation}
                onChange={(e) => {
                  setStreetCityInput(e.target.value);
                  setAddressError("");
                  if (locationValidated) {
                    resetLocationSelection();
                  }
                }}
                placeholder="הזינו רחוב ועיר ובחרו מתוך האפשרויות המוצעות"
              />
              <div className="text-danger">{addressError || null}</div>
            </span>
          </div>
        </div>

        <div className="add-address__row">
          <div className="add-address__cell">
            <Input
              type="text"
              register={register}
              label="שם מקבל ההזמנה"
              placeholder="שם משפחה/שם משרד/שם חברה/שם מוסד"
              errors={errors}
              name="Name"
              className="hz-field--borderless"
            />
          </div>
        </div>

        <div className="add-address__row add-address__row--3">
          <div className="add-address__cell">
            <Input
              type="tel"
              register={register}
              label="מס' בניין*"
              errors={errors}
              name="StreetNumber"
              className="hz-field--borderless"
            />
          </div>
          <div className="add-address__cell">
            <Input
              type="number"
              register={register}
              label="קומה*"
              errors={errors}
              name="Floor"
              className="hz-field--borderless"
            />
          </div>
          <div className="add-address__cell">
            <Input
              type="tel"
              register={register}
              label="מס' דירה*"
              errors={errors}
              name="HouseNumber"
              className="hz-field--borderless"
            />
          </div>
        </div>

        <div className="add-address__row">
          <div className="add-address__cell add-address__cell--zip">
            <Input
              type="tel"
              register={register}
              label="מיקוד"
              errors={errors}
              name="ZipCode"
              className="hz-field--borderless"
            />
          </div>
        </div>
        <span className="checkbox-and-btn">
          <button className="btn" type="submit">
            {"שמור > "}
          </button>
        </span>
      </form>
    </div>
  );
};

export default NewAddress;
